import { createContext, memo, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { getTutorByIesId } from '../utils/network';
import {
  tutorPortalUrl,
  IesTutorPortalUrl,
  sessionScriptSrcByEnv,
  IesClientId,
} from '../utils/utils';
import { Tutor } from '../utils/types';
import { useMixpanelContext } from './MixpanelContext';

interface UserContextProps {
  token: string | undefined;
  tutorByIesId: Tutor | undefined;
  iesId: string | null;
}

const emptyContext: UserContextProps = {
  token: undefined,
  tutorByIesId: undefined,
  iesId: null,
};

const UserContext = createContext(emptyContext);

type UserProviderProps = {
  children: ReactNode;
};

// react memo for preventing re-rendering if variables are unchanged.
export const UserProvider = memo(({ children }: UserProviderProps) => {
  const { mixpanelTrackEvent } = useMixpanelContext();
  const [token, setToken] = useState<string | undefined>(emptyContext.token);
  const [tutorByIesId, setTutorByIesId] = useState<Tutor | undefined>(emptyContext.tutorByIesId);
  const [iesId, setIesId] = useState<string | null>(emptyContext.iesId);

  // STAGE
  // System Account
  // UserName: livtut_iesv2_adm
  // UserPassword: wEaCXVzLWVhc3QtMS

  // OAuth Client
  // clientID: LIVTUT-Client
  // secret: ckNFf6SrLVakb4t3

  // PROD
  // System Account
  // UserName: livtut_iesv2_adm
  // UserPassword: Iu7bT6moaerV2pLd

  // OAuth Client
  // clientID: LIVTUT-Client
  // secret: Muy5f6SUewakb4v2

  //  https://teach.tutoring-stg.pearsondev.tech/()=%3E%22prod%22===ts()?%22https%3A%2F%2Fiam.pearson.com%2Fauth%2Foauth2%2Frealms%2Fpearson%2Fauthorize%3Fservice=AuthN&client_id=LIVTUT-Client&scope=openid&response_type=code&redirect_uri=%22%3A%22https%3A%2F%2Fiam-stage.pearson.com%2Fauth%2Foauth2%2Frealms%2Fpearson%2Fauthorize%3Fservice%3DAuthN&client_id=LIVTUT-Client&scope=openid&response_type=code&redirect_uri=%22https%3A%2F%2Fteach.tutoring-stg.pearsondev.tech%2F

  const [isIesScriptInitialized, setIsIesScriptInitialized] = useState(false);

  function getUserToken(
    callback: (token: string) => void,
    redirectLogin = true,
    onSignOut = () => {},
    retry = 3,
  ) {
    if (window.piSession) {
      const fetchedToken = window.piSession.currentToken();
      if (fetchedToken) {
        if (callback) callback(fetchedToken);
      } else if (retry) {
        setTimeout(() => getUserToken(callback, redirectLogin, onSignOut, retry - 1), 1000);
      } else if (redirectLogin) {
        window.location.replace(`${IesTutorPortalUrl()}${tutorPortalUrl()}`);
      } else {
        console.log('logout because the token is not valid');
        onSignOut();
      }
    } else {
      console.log('No piSession');
    }
  }

  const fetchTutor = () => {
    const fetchTutorByIesId = async (fetchedToken: string) => {
      try {
        if (fetchedToken) {
          const fetchedTutor = await getTutorByIesId(fetchedToken);
          setTutorByIesId(fetchedTutor);
          console.log('tutorByIesId: ', fetchedTutor);
        }
      } catch (err) {
        console.log(err);
      }
    };
    if (process.env.REACT_APP_CONTENT_ENV !== 'prod') {
      console.log(`window.piSession: ${JSON.stringify(window.piSession, null, 2)}`);
    }
    if (window.piSession && isIesScriptInitialized) {
      const userId = window.piSession.userId();
      if (process.env.REACT_APP_CONTENT_ENV !== 'prod') {
        console.log(`userId: ${userId}`);
      }

      if (userId) {
        getUserToken((fetchedToken) => {
          if (fetchedToken) {
            setToken(fetchedToken);
            setIesId(userId);
            fetchTutorByIesId(fetchedToken);
            // Mixpanel metric tutor login:
            mixpanelTrackEvent('TutorPortalLogin', {
              tutor: tutorByIesId,
            });
          } else {
            window.location.replace(`${IesTutorPortalUrl()}${tutorPortalUrl()}`);
          }
        });
      } else {
        window.location.replace(`${IesTutorPortalUrl()}${tutorPortalUrl()}`);
      }
    }
  };

  useEffect(() => {
    fetchTutor();
  }, []);

  useEffect(() => {
    if (!iesId) {
      setTimeout(() => {
        fetchTutor();
      }, 2 * 1000);
    }
  }, [isIesScriptInitialized]);

  useEffect(() => {
    const onIesScriptLoad = () => {
      if (window.piSession) {
        window.piSession.initialize(IesClientId(), {
          requireLogin: false,
          monitorUserActivity: true,
          useDefaultIdleTimoutWarningPopup: true,
          sessionIdleTimeoutWarningSeconds: 300,
          useJwt: true,
          checkSessionMilliseconds: 3e3,
          sessionIdleTimeoutSeconds: 82800,
          autoRefresh: true,
          loginSuccessUrl: process.env.REACT_APP_LOGIN_SUCCESS_URL || tutorPortalUrl(),
        });

        setTimeout(() => {
          console.log('user status timeout');
          setIsIesScriptInitialized(true);
        }, 10000);
        window.addEventListener('sessionstateknown', () => {
          if (process.env.REACT_APP_CONTENT_ENV !== 'prod') {
            console.log('sessionstateknown event listener triggered');
          }
          setIsIesScriptInitialized(true);
        });
      }
    };
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = `${sessionScriptSrcByEnv()}`;
    script.async = true;

    script.onload = onIesScriptLoad;

    script.onerror = () => {
      console.error('Failed to load the script');
    };

    if (!window.piSession && !document.querySelector(`script[src="${sessionScriptSrcByEnv()}"]`)) {
      document.body.appendChild(script);
    }
  }, []);

  const value = useMemo(
    () => ({
      token,
      tutorByIesId,
      iesId,
    }),
    [tutorByIesId, iesId],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
});

export const useUserContext = () => useContext(UserContext);
